import React, { useState } from 'react'
import { TextInput, Textarea, Select, NumberInput, Button, Text, Group, Box, Grid, ActionIcon } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconX, IconCircleX } from '@tabler/icons-react'
import { createStockMovement, searchCouriers, searchOrders } from '../../../services/inventory'

const movementTypeList = [
    { value: 'OUTBOUND', label: 'Outbound' },
    { value: 'INBOUND', label: 'Inbound' },
]

const shipmentMethodList = [
    { value: 'air', label: 'Air Cargo' },
    { value: 'air_dtd', label: 'Air Cargo Door To Door' },
    { value: 'ocean', label: 'Ocean Cargo' },
    { value: 'ocean_dtd', label: 'Ocean Cargo Door To Door' },
    { value: 'official_trucking', label: 'Trucking' },
    { value: 'unofficial_trucking', label: 'Trucking Unofficial' },
    { value: 'air_courier', label: 'Air Courier' },
]

const FormMovement = ({ items, warehouseName, warehouseId, quantityUnitList, onRemoveItem, onUpdateItemQty, onClose, onRefresh }) => {
    const [fulfillmentNumber, setFulfillmentNumber] = useState('')
    const [date, setDate] = useState(new Date().toISOString().split('T')[0])
    const [time, setTime] = useState(new Date().toTimeString().slice(0, 5))
    const [type, setType] = useState('OUTBOUND')
    const [deliveryMethod, setDeliveryMethod] = useState('air')
    const [remarks, setRemarks] = useState('')
    const [loadingSave, setLoadingSave] = useState(false)
    const [courierList, setCourierList] = useState([])
    const [selectedCourier, setSelectedCourier] = useState(null)
    const [orderList, setOrderList] = useState([])
    const [selectedOrder, setSelectedOrder] = useState(null)
    const [courierSearch, setCourierSearch] = useState('')
    const [orderSearch, setOrderSearch] = useState('')

    const handleSearchCouriers = async (search) => {
        setCourierSearch(search)
        if (search.length < 2) return
        try {
            const res = await searchCouriers(search)
            const data = res?.data || []
            setCourierList(data.map(c => ({ value: String(c.attributes.id), label: `${c.attributes.name} - ${c.attributes.tracking_system}` })))
        } catch (error) { console.error(error) }
    }

    const handleSearchOrders = async (search) => {
        setOrderSearch(search)
        if (search.length < 2) return
        try {
            const res = await searchOrders(search)
            const data = res?.data || []
            setOrderList(data.map(o => ({ value: String(o.attributes.id), label: `${o.attributes.id} - ${o.attributes.order_code}` })))
        } catch (error) { console.error(error) }
    }

    const handleSave = async () => {
        if (!date) {
            notifications.show({ title: 'Validation Error', message: 'Date is Required', color: 'red', icon: <IconX /> })
            return
        }
        if (!remarks) {
            notifications.show({ title: 'Validation Error', message: 'Remarks is Required', color: 'red', icon: <IconX /> })
            return
        }
        if (items.length === 0) {
            notifications.show({ title: 'Validation Error', message: 'Items is Required', color: 'red', icon: <IconX /> })
            return
        }

        const hasTime = Boolean(time)
        const remapItems = items.map(item => {
            const qUnit = quantityUnitList.find(e => e.value === item.quantityUnit || e.name === item.quantityUnit)
            return {
                inventory_id: Number(item.id),
                quantity: Number(item.quantity),
                quantity_unit: qUnit ? qUnit.name : item.quantityUnit
            }
        })

        const payload = {
            fulfillment_number: fulfillmentNumber,
            movement_date: `${date} ${hasTime ? time : '00:00'}:00`,
            type,
            warehouse_id: Number(warehouseId),
            delivery_method: deliveryMethod,
            items: remapItems,
            remarks,
        }

        if (selectedOrder) payload.order_id = Number(selectedOrder)
        if (deliveryMethod === 'air_courier' && selectedCourier) payload.courier_id = Number(selectedCourier)

        setLoadingSave(true)
        try {
            const res = await createStockMovement(payload)
            if (res?.errors) {
                const msg = res.errors[0]?.message?.includes('Validation') ? 'Error Validation BE' : res.errors[0]?.message
                notifications.show({ title: 'Error', message: msg, color: 'red', icon: <IconX /> })
            } else {
                notifications.show({ title: 'Success', message: 'Stock Movement created successfully', color: 'teal', icon: <IconCheck /> })
                onClose()
                onRefresh()
            }
        } catch (error) {
            notifications.show({ title: 'Error', message: error.message, color: 'red', icon: <IconX /> })
        } finally {
            setLoadingSave(false)
        }
    }

    return (
        <Box>
            <TextInput label="Fulfillment Number" placeholder="ex. ACN-IB/001" value={fulfillmentNumber}
                onChange={(e) => setFulfillmentNumber(e.target.value)} />

            <Grid mt="sm">
                <Grid.Col span={6}>
                    <TextInput label={<>Movement Date <Text component="span" c="red">*</Text></>} type="date" size="sm"
                        value={date} onChange={(e) => setDate(e.target.value)} />
                </Grid.Col>
                <Grid.Col span={6}>
                    <TextInput label="Movement Time" type="time" size="sm" value={time} onChange={(e) => setTime(e.target.value)} />
                </Grid.Col>
            </Grid>

            <Grid mt="sm">
                <Grid.Col span={6}>
                    <Select label={<>Type <Text component="span" c="red">*</Text></>} size="sm"
                        data={movementTypeList} value={type} onChange={setType} />
                </Grid.Col>
                <Grid.Col span={6}>
                    <TextInput label={<>Warehouse <Text component="span" c="red">*</Text></>} size="sm" disabled value={warehouseName || ''} />
                </Grid.Col>
            </Grid>

            <Select label={<>Delivery Methods <Text component="span" c="red">*</Text></>} size="sm" mt="sm"
                data={shipmentMethodList} value={deliveryMethod} onChange={setDeliveryMethod} />

            {deliveryMethod === 'air_courier' && (
                <Select label="Couriers" size="sm" mt="sm" searchable clearable
                    data={courierList} value={selectedCourier} onChange={setSelectedCourier}
                    onSearchChange={handleSearchCouriers} searchValue={courierSearch}
                    placeholder="Search courier..." />
            )}

            {/* Items */}
            <Box mt="md">
                <Text fw={600} size="sm">Items <Text component="span" c="red">*</Text></Text>
                {items.length === 0 ? (
                    <Box bg="gray.0" p="sm" ta="center" style={{ borderRadius: 8 }} mt="xs">
                        <Text c="dimmed" size="sm">Items is Empty, please Add your items first</Text>
                    </Box>
                ) : (
                    <Box mt="xs">
                        {items.map(item => (
                            <Box key={item.id} p="sm" mb="xs" style={{ border: '1px solid #dee2e6', borderRadius: 8, position: 'relative' }} bg="gray.0">
                                <Grid>
                                    <Grid.Col span={6}>
                                        <TextInput label="Product Name" size="sm" disabled value={item.productName} />
                                    </Grid.Col>
                                    <Grid.Col span={6}>
                                        <NumberInput label="Quantity" size="sm" min={1} value={item.quantity}
                                            onChange={(v) => onUpdateItemQty(item.id, v)}
                                            suffix={` ${item.quantityUnit}`} />
                                    </Grid.Col>
                                </Grid>
                                <ActionIcon variant="subtle" color="red" size="xs"
                                    style={{ position: 'absolute', top: 4, right: 4 }}
                                    onClick={() => onRemoveItem(item.id)}>
                                    <IconCircleX size={18} />
                                </ActionIcon>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            <Textarea label={<>Notes or Remarks <Text component="span" c="red">*</Text></>} mt="sm" value={remarks} onChange={(e) => setRemarks(e.target.value)} />

            <Select label="Order Reference" size="sm" mt="sm" searchable clearable
                data={orderList} value={selectedOrder} onChange={setSelectedOrder}
                onSearchChange={handleSearchOrders} searchValue={orderSearch}
                placeholder="Search by Order Number" />

            <Group justify="flex-end" mt="lg">
                <Button loading={loadingSave} onClick={handleSave}>Save</Button>
            </Group>
        </Box>
    )
}

export default FormMovement
